import { HTMLAttributes, PropsWithChildren } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faCircleInfo,
  faCircleCheck,
  faCircleExclamation,
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/sharp-regular-svg-icons";
import classNames from "classnames";

import styles from "./toast.module.scss";

type ToastVariant = "info" | "success" | "error" | "warning";

export type ToastProps = Omit<HTMLAttributes<HTMLDivElement>, "className"> & {
  variant?: ToastVariant;
  onDismiss?: () => void;
  isVisible?: boolean;
};

const variantIconMap: Record<ToastVariant, IconDefinition> = {
  info: faCircleInfo,
  success: faCircleCheck,
  error: faCircleExclamation,
  warning: faTriangleExclamation,
};

export const Toast = ({ variant, onDismiss, isVisible = false, children, ...rest }: PropsWithChildren<ToastProps>) => {
  if (!isVisible) {
    return null;
  }

  const icon = variant ? variantIconMap[variant] : faCircleInfo;

  return (
    <div
      className={classNames(styles.toast, {
        [styles.variantInfo]: variant === "info" || variant === undefined,
        [styles.variantSuccess]: variant === "success",
        [styles.variantError]: variant === "error",
        [styles.variantWarning]: variant === "warning",
      })}
      {...rest}
    >
      <div className={styles.iconContainer}>
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className={styles.content}>{children}</div>
      {onDismiss && (
        <button type="button" aria-label="Dismiss" className={styles.dismissButton} onClick={onDismiss}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
      )}
    </div>
  );
};
